import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Center, Column, ConfirmPopup, Row, Text } from '~/commonComponents';
import { useAppSelector } from '~/hooks';
import { AddMoreWorkingInterviewButton } from '../AddMoreWorkingInterviewButton';
import { Colors } from '~/themes/colors';
import { JobPreviewCard } from '~/commonComponents/JobPreviewCard';
import { useDeleteWorkingInterview } from './useDeleteWorkingInterview';
import WarningIcon from '~/images/icons/Warning';

type TRouteParams = {
  id: string;
};

export const WorkingInterviewPreviewer = () => {
  const navigate = useNavigate();
  const { id: jobPostingId } = useParams<TRouteParams>();

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.workingInterviews[jobPostingId] ?? [];
  });

  const fmtOrderedInterviews = useMemo(
    () => [...interviews].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [interviews],
  );

  const totalInterviews = interviews.length;

  const {
    isWarningOpen,
    onCloseWarning,
    onDelete: handleDeleteClick,
  } = useDeleteWorkingInterview({ jobPostingId });

  const handleEditClick = (id: string) =>
    navigate(`/permanent-jobs/postings/${jobPostingId}/working-interviews/${id}/edit`);

  if (!jobPostingId) return null;

  return (
    <>
      <Column marginInline="auto" width={892}>
        <Row alignItems="center" justifyContent="space-between" paddingInline={40}>
          <Text bold fontSize={22}>
            Working Interviews ({totalInterviews})
          </Text>
          <AddMoreWorkingInterviewButton jobPostingId={jobPostingId} />
        </Row>
        <Text color={Colors.neutral_500} italic marginTop={20} textAlign="center">
          More working interviews lead to more candidates, increasing your chances of finding the
          perfect fit!
        </Text>
        <Box
          columnGap={20}
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          marginTop={24}
          rowGap={16}
        >
          {fmtOrderedInterviews.map((it) => (
            <JobPreviewCard
              breakTime={it.breakTime || ''}
              date={new Date(it.date)}
              endsAt={it.hoursDeparture}
              hourlyRate={it.hourlyRate}
              isAssistedHygiene={it.isAssistedHygiene}
              isBreakTimePaid={it.isPaidBreakTime}
              isHideTips
              key={it.id}
              profession="RDH"
              specialty={it.specialty}
              startsAt={it.hoursArrival}
              testName="working-interviews.jobs"
              onDelete={() => handleDeleteClick(it.id)}
              onEdit={() => handleEditClick(it.id)}
            />
          ))}
        </Box>
      </Column>

      {isWarningOpen && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Center marginBottom={20}>
              <WarningIcon width={80} height={80} color={Colors.warning_500} />
            </Center>
          )}
          title="Working Interview Needed to Post Job"
          description="You must keep at least one working interview to post this job. Adding more interviews gives you even better chances to fill the position successfully."
          rightButtonText="Close"
          rightButtonAction={onCloseWarning}
        />
      )}
    </>
  );
};
